import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { FiFilter } from 'react-icons/fi';
import { RadixButton, RadixSpinner, RadixTable, RadixTooltip } from '@/components/radix';
import type { ColumnType, RowType } from '@/components/radix';
import { useFetchDoctorsList } from '@/hooks/api/doctorPortal';
import { api } from '@/utils/Api/POS';
import { useSelector } from 'react-redux';
import type { RootState } from '@/redux/slices/slices.types';
import {
    GetApiDoctorsPortalLogs200,
    GetApiDoctorsPortalLogs200LogsItem,
    GetApiDoctorsPortalDoctors200DoctorsItem,
    GetApiDoctorsPortalLogs200LogsItemAction,
} from '@/shared/api/models';
import { GetApiDoctorsPortalDoctorsStatus } from '@/shared/api/models/getApiDoctorsPortalDoctorsStatus';
import { formatDate } from '@/utils/dateFormatter';
import ChevronRightIcon from '@/assets/Marketing/ChevronRight.svg';
import LogsFilterModal, { type LogsViewState } from './components/LogsFilterModal';
import { GetApiDoctorsPortalLogsSortBy } from '@/shared/api/models/getApiDoctorsPortalLogsSortBy';
import { GetApiDoctorsPortalLogsSortOrder } from '@/shared/api/models/getApiDoctorsPortalLogsSortOrder';

const DOCTOR_ALL_VALUE = '__all__';
const PAGE_SIZE = 10;
const LINKED_DOCTORS_LIMIT = 100;

const defaultViewState: LogsViewState = {
    doctorId: DOCTOR_ALL_VALUE,
    sortBy: GetApiDoctorsPortalLogsSortBy.createdAt,
    sortOrder: GetApiDoctorsPortalLogsSortOrder.desc,
};

export default function DoctorLogs() {
    const [page, setPage] = useState(1);
    const [viewState, setViewState] = useState<LogsViewState>(defaultViewState);
    const [filterModalOpen, setFilterModalOpen] = useState(false);

    const actionMap = {
        [GetApiDoctorsPortalLogs200LogsItemAction.PRIORITY_CHANGED]: t('Doctors.ChangedPriority'),
        [GetApiDoctorsPortalLogs200LogsItemAction.PINNED]: t('Doctors.Pinned'),
        [GetApiDoctorsPortalLogs200LogsItemAction.UNPINNED]: t('Doctors.Unpinned'),
        [GetApiDoctorsPortalLogs200LogsItemAction.INVITED]: t('Doctors.Invited'),
        [GetApiDoctorsPortalLogs200LogsItemAction.ACCEPTED]: t('Doctors.Accepted'),
        [GetApiDoctorsPortalLogs200LogsItemAction.REJECTED]: t('Doctors.StatusRejected'),
        [GetApiDoctorsPortalLogs200LogsItemAction.UNLINKED]: t('Doctors.Unlinked'),
        [GetApiDoctorsPortalLogs200LogsItemAction.CREATED]: t('Common.CreatedAt'),
        [GetApiDoctorsPortalLogs200LogsItemAction.UPDATED]: t('Customer.Updated'),
        [GetApiDoctorsPortalLogs200LogsItemAction.DELETED]: t('Common.Deleted'),
    };

    const fetchParams = useMemo(
        () => ({
            page: 1,
            limit: LINKED_DOCTORS_LIMIT,
            status: GetApiDoctorsPortalDoctorsStatus.LINKED,
        }),
        [],
    );
    useFetchDoctorsList(fetchParams);
    const { doctors } = useSelector((state: RootState) => state.doctorsList);

    const doctorOptions = useMemo(() => {
        const list = (doctors || []) as GetApiDoctorsPortalDoctors200DoctorsItem[];
        const options = [{ value: DOCTOR_ALL_VALUE, label: t('Common.All') }];
        list.forEach((d) => {
            options.push({
                value: d.uuid,
                label: `${d.firstName} ${d.lastName}`.trim() || d.email,
            });
        });
        return options;
    }, [doctors]);

    const doctorUuidMap = useMemo(() => {
        const list = (doctors || []) as GetApiDoctorsPortalDoctors200DoctorsItem[];
        const map: Record<string, string> = {};
        list.forEach((d) => {
            map[d.uuid] = `${d.firstName} ${d.lastName}`.trim() || d.email;
        });
        return map;
    }, [doctors]);

    const logsQueryParams = useMemo(
        () => ({
            page,
            limit: PAGE_SIZE,
            doctorUuid: viewState.doctorId === DOCTOR_ALL_VALUE ? undefined : viewState.doctorId,
            sortBy: viewState.sortBy as GetApiDoctorsPortalLogsSortBy | undefined,
            sortOrder: (viewState.sortOrder as GetApiDoctorsPortalLogsSortOrder) || undefined,
            fromDate: viewState.fromDate,
            toDate: viewState.toDate,
        }),
        [page, viewState.doctorId, viewState.sortBy, viewState.sortOrder, viewState.fromDate, viewState.toDate],
    );

    const logsQuery = useQuery<GetApiDoctorsPortalLogs200>({
        queryKey: ['doctors-portal-logs', logsQueryParams],
        queryFn: () => api.getApiDoctorsPortalLogs(logsQueryParams),
    });

    const logs = logsQuery.data?.logs ?? [];

    const pagination = useMemo(() => {
        const currentPage = logsQuery.data?.currentPage ?? page;
        const totalPages = logsQuery.data?.totalPages ?? 0;
        const total = logsQuery.data?.total ?? 0;
        return {
            currentPage,
            totalPages,
            total,
            hasNextPage: currentPage < totalPages,
            hasPreviousPage: currentPage > 1,
        };
    }, [logsQuery.data?.currentPage, logsQuery.data?.totalPages, logsQuery.data?.total, page]);

    const columns = useMemo<ColumnType[]>(
        () => [
            {
                id: 'doctor',
                name: t('Common.Name'),
                selector: (row: RowType) => {
                    const log = row as GetApiDoctorsPortalLogs200LogsItem;
                    const name = doctorUuidMap[log.doctorUuid] ?? '';
                    return <span className="text-text-primary text-sm truncate max-w-[160px] block">{name}</span>;
                },
            },

            {
                id: 'action',
                name: t('Doctors.LogsAction'),
                selector: (row: RowType) => {
                    const log = row as GetApiDoctorsPortalLogs200LogsItem;
                    return <span className="text-text-primary text-sm">{actionMap[log.action] ?? '—'}</span>;
                },
            },
            {
                id: 'actionData',
                name: t('Doctors.LogsActionData'),
                selector: (row: RowType) => {
                    const log = row as GetApiDoctorsPortalLogs200LogsItem;
                    const data = log.actionData;
                    const text =
                        data != null && typeof data === 'object'
                            ? JSON.stringify(data)
                            : data != null
                              ? String(data)
                              : '—';
                    return (
                        <RadixTooltip title={text}>
                            <span className="text-text-secondary text-sm truncate max-w-[200px] block">{text}</span>
                        </RadixTooltip>
                    );
                },
            },
            {
                id: 'performedBy',
                name: t('Doctors.LogsPerformedBy'),
                selector: (row: RowType) => {
                    const log = row as GetApiDoctorsPortalLogs200LogsItem;
                    return <span className="text-text-secondary text-sm">{log.performedByEmployeeName ?? '—'}</span>;
                },
            },
            {
                id: 'createdAt',
                name: t('Common.CreatedAt'),
                selector: (row: RowType) => {
                    const log = row as GetApiDoctorsPortalLogs200LogsItem;
                    return (
                        <span className="text-text-secondary text-sm whitespace-nowrap">
                            {log.createdAt ? formatDate(log.createdAt, 'DD/MM-YYYY HH:mm') : '—'}
                        </span>
                    );
                },
            },
            {
                id: 'deviceIpAddress',
                name: t('Doctors.LogsDeviceIp'),
                selector: (row: RowType) => {
                    const log = row as GetApiDoctorsPortalLogs200LogsItem;
                    return <span className="text-text-secondary text-sm">{log.deviceIpAddress ?? '—'}</span>;
                },
            },
        ],
        [doctorUuidMap],
    );

    const paginationComponent = () => {
        if (!pagination || pagination.totalPages <= 1) return null;
        return (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-1 pt-4 pb-0 border-t border-border-default mt-4">
                <div className="text-sm text-text-secondary">
                    {t('Common.Showing')} {logs.length} {t('Common.of')} {pagination.total} {t('Common.items')}
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-text-secondary">
                        {t('Common.Page')} {pagination.currentPage} {t('Common.of')} {pagination.totalPages}
                    </span>
                    <RadixButton
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        iconOnly
                        disabled={!pagination.hasPreviousPage || logsQuery.isLoading}
                    >
                        <img src={ChevronRightIcon} alt="Previous" className="w-4 h-4 rotate-[180deg]" />
                    </RadixButton>
                    <RadixButton
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => p + 1)}
                        iconOnly
                        disabled={!pagination.hasNextPage || logsQuery.isLoading}
                    >
                        <img src={ChevronRightIcon} alt="Next" className="w-4 h-4" />
                    </RadixButton>
                </div>
            </div>
        );
    };

    const handleApplyFilter = useCallback((state: LogsViewState) => {
        setViewState(state);
        setPage(1);
    }, []);

    return (
        <section className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-text-primary m-0 md:mb-1">
                        {t('Doctors.LogsPageTitle')}
                    </h1>
                    <p className="text-sm text-text-secondary m-0">{t('Doctors.LogsPageDescription')}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <RadixButton
                        variant="outline"
                        size="sm"
                        onClick={() => setFilterModalOpen(true)}
                        className="w-full sm:max-w-fit flex items-center justify-center gap-2"
                    >
                        <FiFilter className="w-4 h-4" />
                        {t('Doctors.FilterAndSort')}
                    </RadixButton>
                </div>
            </div>

            {logsQuery.isLoading && (
                <div className="flex justify-center py-32 border border-border-default border-solid rounded-md">
                    <RadixSpinner size="md" />
                </div>
            )}

            {!logsQuery.isLoading && logs.length === 0 && (
                <div className="text-center py-32 border border-border-default border-solid rounded-md">
                    <p className="text-text-secondary m-0">{t('Customer.NoLogs')}</p>
                </div>
            )}

            {!logsQuery.isLoading && logs.length > 0 && (
                <div className="border border-border-default border-solid rounded-md overflow-hidden">
                    <RadixTable columns={columns} data={logs.map((log) => ({ ...log, id: log.logId }))} />
                </div>
            )}

            {paginationComponent()}

            <LogsFilterModal
                open={filterModalOpen}
                onOpenChange={setFilterModalOpen}
                value={viewState}
                onApply={handleApplyFilter}
                doctorOptions={doctorOptions}
            />
        </section>
    );
}
